import React from 'react';
import { View } from 'react-native';
import { MealLogCard } from './MealLogCard';
import { PresentationCaption } from './PresentationCaption';

interface MealLogGroup {
  caption?: string | null;
  mealLogIds: string[];
}

interface MealLogListPayload {
  groups?: MealLogGroup[];
  mealLogIds?: string[];
}

export interface MealLogListCardProps {
  payload: Record<string, unknown>;
}

/** Normalise either payload shape into a list of groups. */
const toGroups = (payload: Record<string, unknown>): MealLogGroup[] => {
  const p = payload as MealLogListPayload;
  if (Array.isArray(p.groups)) return p.groups;
  if (Array.isArray(p.mealLogIds)) return [{ mealLogIds: p.mealLogIds }];
  return [];
};

/**
 * One captioned block: a group caption (rendered once) followed by the editable
 * logged-meal card for each id. Each card reuses `MealLogCard.Synced`, which owns
 * its own PowerSync/GraphQL fetch and update/delete mutations.
 */
const MealLogGroupBlock: React.FC<MealLogGroup> = ({
  caption,
  mealLogIds = [],
}) => (
  <View className="w-full gap-2">
    <PresentationCaption caption={caption} />
    {mealLogIds.map((id) => (
      <MealLogCard.Synced key={id} payload={{ mealLogId: id }} />
    ))}
  </View>
);

/**
 * `present_meal_logs` → one or more captioned groups of editable logged-meal
 * cards (portion stepper, unit picker, delete). Delegates each entry to the same
 * card used by `present_meal_log` and the Log Meal screen.
 */
export const MealLogListCard: React.FC<MealLogListCardProps> = ({
  payload,
}) => {
  const groups = toGroups(payload);

  if (groups.length === 0) {
    return null;
  }

  return (
    <View className="w-full py-2 gap-3">
      {groups.map((group, index) => (
        <MealLogGroupBlock
          key={index}
          caption={group.caption}
          mealLogIds={group.mealLogIds ?? []}
        />
      ))}
    </View>
  );
};
